/**
 * Paper slips: something just happened, said once, pinned top-right and
 * gone by itself (life-layer.md §2, §3 P1). Replaces the old notification
 * banner.
 *
 * Module-scoped so any store can pin one without threading a prop through the
 * tree; `ui/motion/SlipHost.vue`, mounted once in `App.vue`, draws them. At
 * most three show at once: a fourth sends the oldest away. The player may
 * dismiss one with a tap.
 */
import { ref } from 'vue';

export type SlipTone = 'notice' | 'reward' | 'error';

export interface SlipInput {
  text: string;
  /** A second, quieter line. */
  detail?: string;
  tone?: SlipTone;
  /** Milliseconds until it goes; defaults by tone. */
  ms?: number;
}

export interface Slip {
  id: number;
  text: string;
  detail?: string;
  tone: SlipTone;
}

/** life-layer.md §3 P1: three on the wall, 3.5 s each, errors stay 6 s so they can be read. */
export const MAX_SLIPS = 3;
export const SLIP_MS = 3500;
export const ERROR_SLIP_MS = 6000;

let nextId = 0;
const timers = new Map<number, ReturnType<typeof setTimeout>>();
export const slips = ref<Slip[]>([]);

export function dismissSlip(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) clearTimeout(timer);
  timers.delete(id);
  slips.value = slips.value.filter((s) => s.id !== id);
}

export function pushSlip(input: SlipInput): number {
  const id = ++nextId;
  const tone = input.tone ?? 'notice';
  slips.value = [...slips.value, { id, text: input.text, detail: input.detail, tone }];
  while (slips.value.length > MAX_SLIPS) dismissSlip(slips.value[0]!.id);
  timers.set(
    id,
    setTimeout(() => dismissSlip(id), input.ms ?? (tone === 'error' ? ERROR_SLIP_MS : SLIP_MS)),
  );
  return id;
}

/** Sign-out and tests: take everything down. */
export function clearSlips(): void {
  [...slips.value].forEach((s) => dismissSlip(s.id));
}
